import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Compass, CalendarDays, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/motion";
import { useEnquiry } from "@/context/EnquiryContext";
import { getDestination } from "@/lib/api";

export default function DestinationDetail() {
  const { slug } = useParams();
  const [dest, setDest] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { openEnquiry } = useEnquiry();

  useEffect(() => {
    window.scrollTo(0, 0); setDest(null); setNotFound(false);
    getDestination(slug).then(setDest).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="pt-40 pb-40 text-center"><h1 className="font-serif text-4xl text-[#3b1a1a]">Destination not found</h1><Link to="/destinations" className="mt-6 inline-block text-[#7c1d1d] uppercase tracking-[0.2em] text-xs">← All destinations</Link></div>;
  if (!dest) return <div className="pt-40 pb-40 text-center text-[#5a4b4b]">Loading…</div>;

  const schema = { "@context": "https://schema.org", "@type": "TouristDestination", name: dest.name, description: dest.overview, image: dest.banner_image };

  return (
    <>
      <Seo title={dest.seo?.meta_title || `${dest.name} Travel Guide`} description={dest.seo?.meta_description || dest.overview} image={dest.banner_image} schema={schema} />
      <PageHero title={dest.name} subtitle={dest.region} image={dest.banner_image} crumbs={[{ label: "Destinations", to: "/destinations" }, { label: dest.name }]} />

      <section className="py-16 sm:py-24 bg-[#fdfbf7]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 space-y-14">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a]">About {dest.name}</h2>
              <p className="mt-5 text-[#4a4a4a] text-lg leading-relaxed">{dest.overview}</p>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {dest.things_to_do?.length > 0 && (
                <Reveal>
                  <h3 className="font-serif text-2xl text-[#3b1a1a] flex items-center gap-2"><Compass className="w-5 h-5 text-[#14523d]" /> Things to Do</h3>
                  <ul className="mt-4 space-y-2.5">{dest.things_to_do.map((x, i) => <li key={i} className="flex items-start gap-2.5 text-[#4a4a4a]"><span className="w-1.5 h-1.5 rotate-45 bg-[#d4af37] mt-2 shrink-0" /> {x}</li>)}</ul>
                </Reveal>
              )}
              {dest.places_to_visit?.length > 0 && (
                <Reveal>
                  <h3 className="font-serif text-2xl text-[#3b1a1a] flex items-center gap-2"><MapPin className="w-5 h-5 text-[#7c1d1d]" /> Places to Visit</h3>
                  <ul className="mt-4 space-y-2.5">{dest.places_to_visit.map((x, i) => <li key={i} className="flex items-start gap-2.5 text-[#4a4a4a]"><span className="w-1.5 h-1.5 rotate-45 bg-[#d4af37] mt-2 shrink-0" /> {x}</li>)}</ul>
                </Reveal>
              )}
            </div>

            {dest.gallery?.length > 0 && (
              <Reveal>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#3b1a1a]">Gallery</h3>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {dest.gallery.map((g, i) => <div key={i} className="relative overflow-hidden aspect-[4/3] group border border-[#d4af37]/25"><img src={g} alt={`${dest.name} ${i}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /></div>)}
                </div>
              </Reveal>
            )}

            {dest.nearby_tours?.length > 0 && (
              <Reveal>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#3b1a1a]">Tours featuring {dest.name}</h3>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {dest.nearby_tours.map((t) => (
                    <Link key={t.slug} to={`/tours/${t.slug}`} className="group flex gap-4 bg-white border border-[#d4af37]/25 hover:border-[#d4af37]/60 transition-colors p-3">
                      <div className="w-24 h-24 shrink-0 overflow-hidden"><img src={t.banner_image} alt={t.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /></div>
                      <div className="flex flex-col justify-center">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[#14523d]">{t.category}</div>
                        <div className="font-serif text-lg text-[#3b1a1a] leading-tight group-hover:text-[#7c1d1d] transition-colors clamp-2">{t.name}</div>
                        {t.duration_label && <div className="text-xs text-[#5a4b4b] mt-1">{t.duration_label}</div>}
                      </div>
                    </Link>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="bg-white border border-[#d4af37]/40 p-7">
                {dest.best_time && <div className="flex items-start gap-3 mb-5"><CalendarDays className="w-5 h-5 text-[#14523d] mt-0.5" /><div><div className="text-[10px] tracking-[0.2em] uppercase text-[#5a4b4b]">Best Time to Visit</div><div className="font-serif text-xl text-[#3b1a1a]">{dest.best_time}</div></div></div>}
                <h3 className="font-serif text-2xl text-[#3b1a1a] leading-tight">Plan a trip to {dest.name}</h3>
                <button onClick={() => openEnquiry({ preferred_tour: `${dest.name} tour` })} data-testid="dest-enquire" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#7c1d1d] hover:bg-[#5a1515] text-white px-6 py-4 text-xs tracking-[0.24em] uppercase transition-colors">Enquire Now <ArrowRight className="w-4 h-4" /></button>
              </div>
              {dest.map_embed && <div className="border border-[#d4af37]/40 overflow-hidden aspect-square"><iframe title="map" src={dest.map_embed} className="w-full h-full" loading="lazy" /></div>}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
